// An image's calibration, as stored by the backend ("LangermanOriginal",
// "LangermanPattern", "LangermanWeb", "LangermanWebLike", "LeibishWeb", ...).
//
// Every rule here matches on the suffix rather than the whole value, so further
// variants — another source's web images, say — behave correctly without being
// listed. This mirrors how the backend classifies them (Diamond.HasWebImage and
// friends).

// Only originals go through the analysis pipeline, so only they have an ImageAnalysis.
// A pattern is calibrated too, but the calibrated image is asked for directly rather
// than deduced from the calibration.
export const isAnalyzed = (calibration?: string | null): boolean =>
  calibration?.endsWith("Original") === true;

// A pattern carries the printed color card. It is calibrated but not analyzed, and its stone
// is boxed by the same model the originals use — so a pattern box can be shown and corrected too.
export const isPattern = (calibration?: string | null): boolean =>
  calibration?.endsWith("Pattern") === true;

// The pictures a stone's color is graded from: its originals and its patterns, each ranked
// against the color models on its own.
export const isGraded = (calibration?: string | null): boolean =>
  isAnalyzed(calibration) || isPattern(calibration);

// Display order: original, pattern, web, weblike, then anything unrecognised.
export const calibrationRank = (calibration?: string | null): number => {
  if (!calibration) return 4;
  if (calibration.endsWith("Original")) return 0;
  if (calibration.endsWith("Pattern")) return 1;
  if (calibration.endsWith("WebLike")) return 3;
  if (calibration.endsWith("Web")) return 2;
  return 4;
};

type HasCalibration = { calibration?: string | null; origin?: string | null };

// A stone can have several pictures of a type ("ID123_pattern.JPG", "ID123_pattern2.JPG", ...).
// Within a type they come in the order of their names, shortest first, as the backend takes them.
export const byCalibration = (a: HasCalibration, b: HasCalibration): number =>
  calibrationRank(a.calibration) - calibrationRank(b.calibration) ||
  (a.origin?.length ?? 0) - (b.origin?.length ?? 0) ||
  (a.origin ?? "").localeCompare(b.origin ?? "");

// Which take of its type a picture is: "2" for "ID123_pattern2.JPG" or "ID123_camera_2.JPG", and
// nothing for the plain "ID123_pattern.JPG" or any other spelling.
export const pictureTake = (origin?: string | null): string => {
  const stem = origin?.replace(/\.[^.]*$/, "") ?? "";
  const match = stem.match(/_(?:camera|pattern)_?(\d+)$/i);
  return match ? match[1] : "";
};

// How a picture is named on screen: its calibration without the stone's source — "LangermanOriginal"
// reads as "Original" — and, when it is a further take of its type, which one: "Pattern 2".
export const pictureLabel = (
  image: HasCalibration,
  source?: string | null,
): string => {
  const calibration = image.calibration;
  if (!calibration) return "Unknown";
  const label =
    source && calibration.startsWith(source)
      ? calibration.slice(source.length) || calibration
      : calibration;
  const take = pictureTake(image.origin);
  return take ? `${label} ${take}` : label;
};
